const DEFAULT_VERDONK_APPS_URL = "https://apps.verdonkbroccoli.nl";
const DEFAULT_TIMEOUT_MS = 5000;

export class VerdonkAppsAuthError extends Error {
  constructor(
    message,
    { status = 0, code = "verdonk-apps/auth-failed", cause } = {},
  ) {
    super(message);
    this.name = "VerdonkAppsAuthError";
    this.status = status;
    this.code = code;
    if (cause) this.cause = cause;
  }
}

export const verdonkAppsSsoEnabled =
  process.env.REACT_APP_VERDONK_APPS_SSO === "true";

export const getVerdonkAppsUrl = () => {
  const configuredUrl = String(
    process.env.REACT_APP_VERDONK_APPS_URL || DEFAULT_VERDONK_APPS_URL,
  )
    .trim()
    .replace(/\/+$/, "");

  if (!/^https:\/\//i.test(configuredUrl) || /\/api$/i.test(configuredUrl)) {
    throw new VerdonkAppsAuthError(
      "De URL van Verdonk Apps is niet geldig geconfigureerd.",
      { code: "verdonk-apps/invalid-url" },
    );
  }

  return configuredUrl;
};

const readResponseBody = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const requestJson = async (url, options, fetchImplementation) => {
  let response;
  try {
    response = await fetchImplementation(url, options);
  } catch (cause) {
    const timedOut = cause?.name === "AbortError";
    throw new VerdonkAppsAuthError(
      timedOut
        ? "Automatisch aanmelden via Verdonk Apps duurde te lang."
        : "Verdonk Apps is niet bereikbaar.",
      {
        code: timedOut
          ? "verdonk-apps/timeout"
          : "verdonk-apps/network-error",
        cause,
      },
    );
  }

  const body = await readResponseBody(response);
  if (!response.ok) {
    throw new VerdonkAppsAuthError(
      typeof body.error === "string"
        ? body.error
        : "Automatisch aanmelden via Verdonk Apps is mislukt.",
      {
        status: response.status,
        code: `verdonk-apps/http-${response.status}`,
      },
    );
  }

  return body;
};

export const getVerdonkAppsNoticeKey = (error) => {
  if (error?.status === 401) return "signedOut";
  if (error?.status === 403) return "accessDenied";
  if (error?.status === 404 || error?.status === 503) {
    return "configurationUnavailable";
  }
  return "unavailable";
};

/**
 * Exchanges the Verdonk Apps session cookie for a short-lived Firebase token.
 * The token is returned to Firebase Auth directly and must never be persisted.
 */
export async function requestKistenToken({
  fetchImplementation = fetch,
  baseUrl = getVerdonkAppsUrl(),
  expectedProjectId,
  timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) {
  const abortController =
    typeof AbortController === "function" ? new AbortController() : null;
  const timeoutId = abortController
    ? setTimeout(() => abortController.abort(), timeoutMs)
    : null;
  const requestOptions = {
    credentials: "include",
    ...(abortController ? { signal: abortController.signal } : {}),
  };

  try {
    const csrfBody = await requestJson(
      `${baseUrl}/api/auth/csrf`,
      requestOptions,
      fetchImplementation,
    );
    if (typeof csrfBody.csrfToken !== "string" || !csrfBody.csrfToken) {
      throw new VerdonkAppsAuthError(
        "Verdonk Apps gaf geen geldig CSRF-token terug.",
        { code: "verdonk-apps/invalid-csrf-response" },
      );
    }

    const tokenBody = await requestJson(
      `${baseUrl}/api/auth/firebase-token/kisten`,
      {
        ...requestOptions,
        method: "POST",
        headers: { "X-CSRF-Token": csrfBody.csrfToken },
      },
      fetchImplementation,
    );

    if (typeof tokenBody.token !== "string" || !tokenBody.token) {
      throw new VerdonkAppsAuthError(
        "De Kisten-tokenservice gaf geen geldig antwoord.",
        { code: "verdonk-apps/invalid-token-response" },
      );
    }
    if (tokenBody.appKey && tokenBody.appKey !== "kisten") {
      throw new VerdonkAppsAuthError(
        "De tokenservice verwees naar een andere applicatie.",
        { code: "verdonk-apps/app-mismatch" },
      );
    }
    if (
      expectedProjectId &&
      tokenBody.projectId &&
      tokenBody.projectId !== expectedProjectId
    ) {
      throw new VerdonkAppsAuthError(
        "De tokenservice verwees naar een ander Firebase-project.",
        { code: "verdonk-apps/project-mismatch" },
      );
    }

    return tokenBody;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}
